/**
 * streamManager -- Module-level singleton that OWNS EventSource connections.
 *
 * Connections live in a Map<chatId, Connection> OUTSIDE React. They survive
 * component unmount (navigation). Components subscribe to the Zustand store
 * (useStreamStore) for the reactive projection.
 *
 * Public API:
 *   streamManager.ensureStream(chatId, projectId, opts?)  -- THE ONE connect path
 *   streamManager.send(chatId, projectId, req)            -- optimistic + POST + ensureStream
 *   streamManager.stop(chatId)                            -- user abort
 *   streamManager.clearTurnError(chatId)                  -- dismiss the "turn ended before reply" warning
 *   streamManager.getSlice(chatId)                        -- read current slice
 *   streamManager.attachQueryClient(qc)                   -- call once at app boot
 *
 * Preserves ALL hard-won behaviors: clone-don't-mutate, bounded reconnect,
 * content-aware watchdog, no mid-stream poll flicker, never-stuck recovery.
 */

import type { QueryClient } from '@tanstack/react-query';
import {
  sendMessage as sendChatMessage,
  openStream,
  abortStream,
} from '@/services/chatStreamService';
import { projectKeys } from '@/hooks/code/useProjects';
import { projectChatKeys } from '@/hooks/code/useProjectChat';
import { useStreamStore, type ChatStreamSlice } from '@/stores/useStreamStore';
import { useProjectBusyStore } from '@/stores/useProjectBusyStore';
import { useQuotaStore } from '@/stores/quotaStore';
import { quotaKeys } from '@/hooks/useMyQuotas';
import { applyQuotaSSEEvent } from '@/hooks/useQuotaSSEListener';
import type { Message, PaginatedResponse, ContentBlock } from '@/types/chat.types';
import type { SSEEvent, SendMessageRequest } from '@/types/stream.types';
import type { ComplianceVerdict } from '@/hooks/code/useProjectChat';
// --- FEAT-053 TASK-045 (erro de envio) ---
import { turnErrorInfoFromStreamError } from '@/utils/turnErrorInfo';
// --- fim FEAT-053 TASK-045 (erro de envio) ---

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_CONCURRENT = 20;
const MAX_RECONNECT_ATTEMPTS = 5;
let RECONNECT_DELAYS_MS: readonly number[] = [1000, 2000, 4000, 8000, 15000];
const WATCHDOG_INTERVAL_MS = 5_000;
const WATCHDOG_STALL_THRESHOLD_MS = 30_000;
const SEND_FAILSAFE_MS = 20 * 60_000; // 20 min
/** Id prefix of the user message send() appends before the POST is accepted (FEAT-053 TASK-045, QA-039-A). */
export const OPTIMISTIC_MSG_ID_PREFIX = 'optimistic-';

// ---------------------------------------------------------------------------
// Connection (non-React, module-level)
// ---------------------------------------------------------------------------

interface Connection {
  chatId: string;
  projectId: string;
  abort: AbortController;
  streamMsg: Message | null;
  sawComplete: boolean;
  reconcileFromServer: boolean;
  lastContentAt: number;
  reconnectAttempts: number;
  watchdog: ReturnType<typeof setInterval> | null;
  lastEventId?: string;
  loopPromise: Promise<void>;
}

// ---------------------------------------------------------------------------
// Module-level state (THE durable map)
// ---------------------------------------------------------------------------

const connsByChat = new Map<string, Connection>();
let qc: QueryClient | null = null;

// TEST-ONLY: when true, runConsumeLoop and finalizeTurn bail out immediately.
// Set by _resetForTests(), cleared by ensureStream/send.
let _shuttingDown = false;

// Sending failsafe timers (clear isSending after 20min)
const sendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

// --- FEAT-053 TASK-045 (QA-039-A: envio nao abre a propria conexao SSE) ---
// Chats whose send() POST has not settled yet. While a chat is here, ensureStream is a NO-OP: the server rejects
// a POST with 409 E_MESSAGE_STREAMING when a GET /stream session already exists for the chat, and a connection opened
// before the POST was accepted would outlive a refused send (dots bubble + Stop button stuck on). send() itself
// removes the chat from the set right before its own ensureStream, so the accepted path is unchanged.
const sendsInFlight = new Set<string>();
// --- fim FEAT-053 TASK-045 (QA-039-A) ---

// ---------------------------------------------------------------------------
// Store access helpers (shorthand)
// ---------------------------------------------------------------------------

function getStore() {
  return useStreamStore.getState();
}

function setSlice(chatId: string, partial: Partial<ChatStreamSlice>) {
  getStore()._setSlice(chatId, partial);
  // Sync the legacy busy store for sidebar compatibility
  syncBusyStore(chatId);
}

function ensureSlice(chatId: string, projectId: string) {
  getStore()._ensureSlice(chatId, projectId);
}

function syncBusyStore(chatId: string) {
  const slice = getStore().slicesByChat[chatId];
  if (!slice) return;
  const busy = slice.isStreaming || slice.isSending || slice.status === 'connecting' || slice.status === 'reconnecting';
  useProjectBusyStore.getState().setBusy(slice.projectId, busy);
}

// ---------------------------------------------------------------------------
// Cache helpers (same logic as the old hook, relocated here)
// ---------------------------------------------------------------------------

type Pages = { pages: PaginatedResponse<Message>[]; pageParams: (string | undefined)[] };

function appendMsg(cid: string, msg: Message) {
  if (!qc) return;
  qc.setQueryData<Pages>(projectChatKeys.messages(cid), (old) => {
    if (!old?.pages.length) return { pages: [{ data: [msg], next_cursor: null, has_more: false }], pageParams: [undefined] };
    const pages = [...old.pages];
    pages[0] = { ...pages[0]!, data: [...pages[0]!.data, msg] };
    return { ...old, pages };
  });
}

function updateMsg(cid: string, msg: Message) {
  if (!qc) return;
  qc.setQueryData<Pages>(projectChatKeys.messages(cid), (old) =>
    old ? { ...old, pages: old.pages.map((p) => ({ ...p, data: p.data.map((m) => m.id === msg.id ? msg : m) })) } : old,
  );
}

function replaceOptimisticMsg(cid: string, tempId: string, serverMsg: Message) {
  if (!qc) return;
  qc.setQueryData<Pages>(projectChatKeys.messages(cid), (old) => {
    if (!old) return old;
    let replaced = false;
    const pages = old.pages.map((p) => ({
      ...p,
      data: p.data.map((m) => {
        if (m.id === tempId) { replaced = true; return serverMsg; }
        return m;
      }),
    }));
    if (!replaced) {
      pages[0] = { ...pages[0]!, data: [...pages[0]!.data, serverMsg] };
    }
    return { ...old, pages };
  });
}

function removeOptimisticMsg(cid: string, tempId: string) {
  if (!qc) return;
  qc.setQueryData<Pages>(projectChatKeys.messages(cid), (old) =>
    old ? { ...old, pages: old.pages.map((p) => ({ ...p, data: p.data.filter((m) => m.id !== tempId) })) } : old,
  );
}

// --- FEAT-053 TASK-045 (erro de envio) ---
/** True when the last server-persisted message (sentinel placeholder excluded) is an assistant row. */
function lastPersistedIsAssistant(cid: string): boolean {
  const data = qc?.getQueryData<Pages>(projectChatKeys.messages(cid));
  const msgs = (data?.pages.flatMap((p) => p.data) ?? []).filter(
    (m) => m.sequence_number !== Number.MAX_SAFE_INTEGER,
  );
  return msgs.length > 0 && msgs[msgs.length - 1]!.role === 'assistant';
}
// --- fim FEAT-053 TASK-045 (erro de envio) ---

// ---------------------------------------------------------------------------
// Abortable delay (used by reconnect backoff; resolves on timeout, rejects on abort)
// ---------------------------------------------------------------------------

function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal.aborted) { resolve(); return; }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      // Resolve (not reject) -- the caller checks connsByChat.has() after
      // the delay and bails out if the connection was removed. Rejecting
      // would require a try/catch in finalizeTurn and would change the
      // error-path semantics.
      resolve();
    }
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

// ---------------------------------------------------------------------------
// Watchdog (content-aware; heartbeat does NOT reset)
// ---------------------------------------------------------------------------

function startWatchdog(chatId: string): ReturnType<typeof setInterval> {
  return setInterval(() => {
    const conn = connsByChat.get(chatId);
    if (!conn) return;
    if (conn.lastContentAt && Date.now() - conn.lastContentAt > WATCHDOG_STALL_THRESHOLD_MS) {
      conn.reconcileFromServer = true;
      conn.abort.abort();
    }
  }, WATCHDOG_INTERVAL_MS);
}

// ---------------------------------------------------------------------------
// Event routing (clone-don't-mutate preserved verbatim)
// ---------------------------------------------------------------------------

function routeEvent(chatId: string, ev: SSEEvent) {
  const conn = connsByChat.get(chatId);
  if (!conn) return;

  // Content-producing events reset watchdog + reconnect counter.
  // NOTE: tool_use_complete / tool_result / thinking_complete are included so a
  // long-running tool (build, test) that emits its result late is NOT treated as
  // a stall by the content-aware watchdog.
  const isContentEvent =
    ev.type === 'message_started' ||
    ev.type === 'text_delta' ||
    ev.type === 'thinking_delta' ||
    ev.type === 'thinking_complete' ||
    ev.type === 'tool_use_started' ||
    ev.type === 'tool_use_complete' ||
    ev.type === 'tool_result' ||
    ev.type === 'message_complete';

  if (isContentEvent) {
    conn.lastContentAt = Date.now();
    conn.reconnectAttempts = 0;
  }

  // Track last event ID for resume
  if ('id' in ev && ev.id) {
    conn.lastEventId = ev.id;
  }

  const ref = conn.streamMsg;

  switch (ev.type) {
    case 'message_started': {
      // Resume-on-connect: if this message_id ALREADY EXISTS in the cache,
      // HYDRATE from it (preserve streamed prefix).
      const existingPages = qc?.getQueryData<Pages>(projectChatKeys.messages(chatId));
      const existingMsg = existingPages?.pages
        .flatMap((p) => p.data)
        .find((m) => m.id === ev.data.message_id);

      let msg: Message;
      if (existingMsg && existingMsg.content_blocks.length > 0) {
        msg = {
          ...existingMsg,
          status: 'streaming',
          model_id: existingMsg.model_id ?? ev.data.model_id,
          model_display_name: existingMsg.model_display_name ?? ev.data.model_display_name,
        };
        conn.streamMsg = msg;
        updateMsg(chatId, msg);
      } else {
        msg = {
          id: ev.data.message_id,
          chat_id: chatId,
          parent_message_id: null,
          role: ev.data.role as Message['role'],
          content_blocks: [],
          tokens_in: null,
          tokens_out: null,
          cost_estimate_usd: null,
          latency_ms: null,
          model_id: ev.data.model_id,
          model_display_name: ev.data.model_display_name,
          status: 'streaming',
          finish_reason: null,
          error_code: null,
          error_message: null,
          sequence_number: Number.MAX_SAFE_INTEGER,
          attachments: [],
          created_at: new Date().toISOString(),
        };
        conn.streamMsg = msg;
        appendMsg(chatId, msg);
      }

      // Set startedAt ONCE (never overwrite if already set from startedAtHint)
      const slice = getStore().slicesByChat[chatId];
      const startedAt = slice?.startedAt ?? Date.now();

      setSlice(chatId, {
        status: 'streaming',
        isStreaming: true,
        hasPendingConnect: false,
        streamingMessage: msg,
        currentTurnMessageId: ev.data.message_id,
        startedAt,
        relayStatus: 'online',
        isSending: false,
        turnError: null, // FEAT-053 TASK-045: a reply started, any earlier warning is stale
      });
      break;
    }

    // -----------------------------------------------------------------------
    // CLONE-DON'T-MUTATE: each delta produces a NEW content_blocks array AND
    // a NEW object for the touched block so memoized leaf renderers repaint.
    // -----------------------------------------------------------------------
    case 'text_delta': {
      if (!ref) break;
      const blocks = ref.content_blocks.slice();
      const last = blocks[blocks.length - 1];
      if (last?.type === 'text') {
        blocks[blocks.length - 1] = { ...last, text: last.text + ev.data.delta };
      } else {
        blocks.push({ type: 'text', text: ev.data.delta });
      }
      const next = { ...ref, content_blocks: blocks };
      conn.streamMsg = next;
      updateMsg(chatId, next);
      setSlice(chatId, { streamingMessage: next, hasPendingConnect: false });
      break;
    }

    case 'thinking_delta': {
      if (!ref) break;
      const blocks = ref.content_blocks.slice();
      const last = blocks[blocks.length - 1];
      if (last?.type === 'thinking') {
        blocks[blocks.length - 1] = { ...last, thinking: last.thinking + ev.data.delta };
      } else {
        blocks.push({ type: 'thinking', thinking: ev.data.delta, duration_ms: null, tokens: null });
      }
      const next = { ...ref, content_blocks: blocks };
      conn.streamMsg = next;
      updateMsg(chatId, next);
      setSlice(chatId, { streamingMessage: next, hasPendingConnect: false });
      break;
    }

    case 'tool_use_started': {
      if (!ref) break;
      const blocks: ContentBlock[] = [
        ...ref.content_blocks,
        { type: 'tool_use' as const, id: ev.data.tool_call_id, name: ev.data.tool_name, input: {} },
      ];
      const next = { ...ref, content_blocks: blocks };
      conn.streamMsg = next;
      updateMsg(chatId, next);
      setSlice(chatId, { streamingMessage: next, hasPendingConnect: false });
      break;
    }

    // -----------------------------------------------------------------------
    // thinking_complete: stamp duration/tokens on the last thinking block so
    // the collapsed "Pensou por Xs" label is accurate the moment it collapses.
    // -----------------------------------------------------------------------
    case 'thinking_complete': {
      if (!ref) break;
      const blocks = ref.content_blocks.slice();
      for (let i = blocks.length - 1; i >= 0; i--) {
        const b = blocks[i]!;
        if (b.type === 'thinking') {
          blocks[i] = { ...b, duration_ms: ev.data.duration_ms, tokens: ev.data.tokens };
          break;
        }
      }
      const next = { ...ref, content_blocks: blocks };
      conn.streamMsg = next;
      updateMsg(chatId, next);
      setSlice(chatId, { streamingMessage: next, hasPendingConnect: false });
      break;
    }

    // -----------------------------------------------------------------------
    // tool_use_complete: fill the tool_use block's input with the full object
    // so the human-readable label can show the path/command (was {} while the
    // args were still streaming).
    // -----------------------------------------------------------------------
    case 'tool_use_complete': {
      if (!ref) break;
      const blocks = ref.content_blocks.map((b) =>
        b.type === 'tool_use' && b.id === ev.data.tool_call_id
          ? { ...b, input: ev.data.input }
          : b,
      );
      const next = { ...ref, content_blocks: blocks };
      conn.streamMsg = next;
      updateMsg(chatId, next);
      setSlice(chatId, { streamingMessage: next, hasPendingConnect: false });
      break;
    }

    // -----------------------------------------------------------------------
    // tool_result: append a tool_result block so the UI flips the tool to
    // ok/erro live (the result block carries is_error + duration).
    // -----------------------------------------------------------------------
    case 'tool_result': {
      if (!ref) break;
      const blocks: ContentBlock[] = [
        ...ref.content_blocks,
        {
          type: 'tool_result',
          tool_use_id: ev.data.tool_call_id,
          content: ev.data.output_preview,
          is_error: ev.data.is_error,
          duration_ms: ev.data.duration_ms,
        },
      ];
      const next = { ...ref, content_blocks: blocks };
      conn.streamMsg = next;
      updateMsg(chatId, next);
      setSlice(chatId, { streamingMessage: next, hasPendingConnect: false });
      break;
    }

    case 'message_complete': {
      if (!ref) break;
      const next: Message = {
        ...ref,
        content_blocks: ref.content_blocks.slice(),
        status: 'completed',
        tokens_in: ev.data.tokens_in,
        tokens_out: ev.data.tokens_out,
        cost_estimate_usd: ev.data.cost_usd,
        latency_ms: ev.data.latency_ms,
        finish_reason: ev.data.finish_reason as Message['finish_reason'],
        ...(ev.data.gateway_requests_count !== undefined && {
          gateway_requests_count: ev.data.gateway_requests_count, // FEAT-037
        }),
      };
      conn.streamMsg = next;
      updateMsg(chatId, next);
      conn.sawComplete = true;
      setSlice(chatId, { streamingMessage: next });
      break;
    }

    case 'error': {
      // --- FEAT-053 TASK-045 (erro de envio) ---
      // The turn has no assistant row (no message_started, last persisted message is not an assistant): cover the
      // send path and the orphan auto-connect. A resume with an assistant row (E_WORKSPACE_STOPPED) already shows
      // the failure on the errored row, so no banner there. Quota/budget map to null (own warning).
      if (conn.streamMsg === null && !lastPersistedIsAssistant(chatId)) {
        const info = turnErrorInfoFromStreamError(ev.data);
        if (info) setSlice(chatId, { turnError: info });
      }
      // --- fim FEAT-053 TASK-045 (erro de envio) ---
      conn.reconcileFromServer = true;
      conn.abort.abort();
      break;
    }

    case 'compliance_violation': {
      const slice = getStore().slicesByChat[chatId];
      if (slice) {
        const next = new Map(slice.complianceEvents);
        next.set(ev.data.message_id, {
          verdict: ev.data.action as ComplianceVerdict,
          messageId: ev.data.message_id,
          bannerText: ev.data.banner_text,
          ruleName: ev.data.violation_type,
        });
        setSlice(chatId, { complianceEvents: next });
      }
      break;
    }

    case 'quota_warning':
    case 'quota_exceeded':
    case 'pool_exhausted':
    case 'budget_exceeded': {
      applyQuotaSSEEvent(ev, {
        invalidate: () => void qc?.invalidateQueries({ queryKey: quotaKeys.mine() }),
        setBanner: (b) => useQuotaStore.getState().setExceededBanner(b),
        notifyGrace: (n) => useQuotaStore.getState().setGraceNotice(n),
      });
      break;
    }

    case 'heartbeat': {
      // Heartbeat proves TCP alive but NOT turn progress.
      // Intentionally does NOT reset lastContentAt.
      setSlice(chatId, { relayStatus: 'online' });
      break;
    }

    default:
      break;
  }
}

// ---------------------------------------------------------------------------
// Teardown (cleanup a connection, update store to terminal/idle)
// ---------------------------------------------------------------------------

function teardown(chatId: string, finalStatus: 'terminal' | 'idle' | 'error' = 'terminal') {
  const conn = connsByChat.get(chatId);
  if (conn) {
    if (conn.watchdog) clearInterval(conn.watchdog);
    connsByChat.delete(chatId);
  }

  const slice = getStore().slicesByChat[chatId];
  if (slice) {
    setSlice(chatId, {
      status: finalStatus,
      isStreaming: false,
      hasPendingConnect: false,
      streamingMessage: null,
      isSending: false,
    });
    // Invalidate projects list on busy->idle transition (sidebar update)
    if (qc) {
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
      void qc.invalidateQueries({ queryKey: projectChatKeys.chat(slice.projectId) });
    }
  }

  // Clear sending failsafe
  const timer = sendingTimers.get(chatId);
  if (timer) {
    clearTimeout(timer);
    sendingTimers.delete(chatId);
  }
}

// ---------------------------------------------------------------------------
// finalizeTurn -- never-stuck recovery (relocated from hook's finally block)
// ---------------------------------------------------------------------------

async function finalizeTurn(chatId: string) {
  if (_shuttingDown) return;
  const conn = connsByChat.get(chatId);
  if (!conn) { teardown(chatId); return; }

  if (conn.sawComplete) {
    // Clean completion: reconcile once with server
    if (qc) {
      void qc.invalidateQueries({ queryKey: projectChatKeys.messages(chatId) });
    }
    teardown(chatId, 'terminal');
    return;
  }

  if (conn.reconcileFromServer) {
    // NEVER-STUCK RECOVERY: stream died without message_complete.
    // Refetch server truth ONCE, decide based on persisted status.
    // Invalidate the messages query so a mounted component refetches on its
    // own schedule (no mid-stream flicker). Then read the cache to decide
    // whether to reconnect. IMPORTANT: filter out the manager's own sentinel
    // placeholder (sequence_number === MAX_SAFE_INTEGER) so the decision is
    // based purely on server-persisted data.
    try {
      if (qc) {
        void qc.invalidateQueries({ queryKey: projectChatKeys.messages(chatId) });
      }
      // Read the cache, but EXCLUDE the manager's sentinel placeholder
      const freshData = qc?.getQueryData<Pages>(projectChatKeys.messages(chatId));
      const allMsgs = (freshData?.pages.flatMap((p) => p.data) ?? [])
        .filter((m) => m.sequence_number !== Number.MAX_SAFE_INTEGER);
      let lastAssistant: Message | null = null;
      for (let i = allMsgs.length - 1; i >= 0; i--) {
        if (allMsgs[i]!.role === 'assistant') { lastAssistant = allMsgs[i]!; break; }
      }

      if (lastAssistant?.status === 'streaming') {
        // Turn genuinely still live -- bounded reconnect
        const attempt = conn.reconnectAttempts;
        if (attempt >= MAX_RECONNECT_ATTEMPTS) {
          // Exhausted budget; fall back to poll (degraded, not stuck)
          conn.reconnectAttempts = 0;
          teardown(chatId, 'idle');
          return;
        }
        conn.reconnectAttempts = attempt + 1;
        const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)]!;
        conn.reconcileFromServer = false;
        conn.sawComplete = false;

        setSlice(chatId, { status: 'reconnecting', reconnectAttempts: conn.reconnectAttempts });

        // Delay (exponential backoff) -- abortable so _resetForTests / stop unblocks
        await abortableDelay(delay, conn.abort.signal);

        // Guard: if the connection was removed during the delay (e.g. _resetForTests
        // or user navigated away and stop was called), bail out -- do NOT reconnect.
        if (!connsByChat.has(chatId)) return;

        // Reconnect: fresh AbortController, re-enter consume loop
        conn.abort = new AbortController();
        conn.loopPromise = runConsumeLoop(chatId);
        return; // KEEP conn alive (don't teardown)
      }
      // Terminal (completed/errored/cancelled): refetch already updated cache
      teardown(chatId, 'terminal');
    } catch {
      // Refetch failed (network down). Not stuck -- degraded.
      teardown(chatId, 'error');
    }
    return;
  }

  // User-initiated abort: do NOT refetch (preserves no-mid-stream-flicker fix)
  teardown(chatId, 'idle');
}

// ---------------------------------------------------------------------------
// runConsumeLoop -- the for-await loop (manager-owned, not React-owned)
// ---------------------------------------------------------------------------

async function runConsumeLoop(chatId: string) {
  if (_shuttingDown) return;
  const conn = connsByChat.get(chatId);
  if (!conn) return;

  conn.sawComplete = false;
  conn.reconcileFromServer = false;
  conn.lastContentAt = Date.now();

  setSlice(chatId, {
    status: 'streaming',
    isStreaming: true,
    relayStatus: 'online',
  });

  try {
    for await (const ev of openStream({ chatId, signal: conn.abort.signal, lastEventId: conn.lastEventId })) {
      routeEvent(chatId, ev);
    }
    // Stream ended without message_complete (silent disconnect)
    if (!conn.sawComplete) {
      conn.reconcileFromServer = true;
    }
  } catch (err: unknown) {
    if ((err as Error)?.name !== 'AbortError') {
      setSlice(chatId, { relayStatus: 'offline' });
      conn.reconcileFromServer = true;
    }
  } finally {
    await finalizeTurn(chatId);
  }
}

// ---------------------------------------------------------------------------
// PUBLIC API
// ---------------------------------------------------------------------------

export const streamManager = {
  /**
   * Call once at app bootstrap to provide the QueryClient.
   * This allows the manager to read/write the TanStack Query cache
   * outside the React tree.
   */
  attachQueryClient(queryClient: QueryClient): void {
    qc = queryClient;
    _shuttingDown = false; // Clear test-only shutdown flag on fresh attach
  },

  /**
   * THE ONE CONNECT PATH.
   * Both SEND and AUTO-RESUME funnel here.
   * NO-OP if a live connection already exists for this chatId.
   */
  ensureStream(
    chatId: string,
    projectId: string,
    opts?: { startedAtHint?: number },
  ): void {
    // Already live -> NO-OP (idempotency guard)
    if (connsByChat.has(chatId)) {
      // Patch startedAt if a hint is provided and slice doesn't have one yet
      if (opts?.startedAtHint) {
        const slice = getStore().slicesByChat[chatId];
        if (slice && slice.startedAt === null) {
          setSlice(chatId, { startedAt: opts.startedAtHint });
        }
      }
      return;
    }

    // --- FEAT-053 TASK-045 (QA-039-A) ---
    // A send's POST is still in flight: only send() may open the connection, and only after the POST was accepted.
    if (sendsInFlight.has(chatId)) return;
    // --- fim FEAT-053 TASK-045 (QA-039-A) ---

    // Concurrency cap
    if (connsByChat.size >= MAX_CONCURRENT) {
      // Try to evict the oldest idle/terminal connection
      let evicted = false;
      for (const [id, c] of connsByChat) {
        const slice = getStore().slicesByChat[id];
        if (!slice?.isStreaming) {
          c.abort.abort();
          teardown(id, 'idle');
          evicted = true;
          break;
        }
      }
      if (!evicted) {
        ensureSlice(chatId, projectId);
        setSlice(chatId, { error: 'E_SSE_LIMIT: too many concurrent streams', status: 'error' });
        return;
      }
    }

    // Create slice and connection
    ensureSlice(chatId, projectId);
    setSlice(chatId, {
      status: 'connecting',
      hasPendingConnect: true,
      isStreaming: true,
      startedAt: opts?.startedAtHint ?? getStore().slicesByChat[chatId]?.startedAt ?? null,
    });

    const conn: Connection = {
      chatId,
      projectId,
      abort: new AbortController(),
      streamMsg: null,
      sawComplete: false,
      reconcileFromServer: false,
      lastContentAt: Date.now(),
      reconnectAttempts: 0,
      watchdog: startWatchdog(chatId),
      loopPromise: Promise.resolve(),
    };
    connsByChat.set(chatId, conn);
    conn.loopPromise = runConsumeLoop(chatId);
  },

  /**
   * Send a message: optimistic insert + POST + ensureStream.
   * This is the SEND path -- collapses into the same ensureStream.
   */
  async send(
    chatId: string,
    projectId: string,
    req: { content: string; attachment_ids?: string[]; model_id?: string },
  ): Promise<void> {
    if (!qc) return;

    ensureSlice(chatId, projectId);
    // --- FEAT-053 TASK-045 (erro de envio) ---
    setSlice(chatId, { turnError: null });
    // --- fim FEAT-053 TASK-045 (erro de envio) ---

    // Get current messages for sequence_number calculation
    const currentData = qc.getQueryData<Pages>(projectChatKeys.messages(chatId));
    const allMsgs = currentData?.pages.flatMap((p) => p.data) ?? [];
    const maxSeq = allMsgs.length > 0 ? allMsgs[allMsgs.length - 1]!.sequence_number : 0;

    // Optimistic user message
    const tempId = `${OPTIMISTIC_MSG_ID_PREFIX}${Date.now()}`;
    const optimistic: Message = {
      id: tempId,
      chat_id: chatId,
      parent_message_id: null,
      role: 'user',
      content_blocks: [{ type: 'text', text: req.content }],
      tokens_in: null,
      tokens_out: null,
      cost_estimate_usd: null,
      latency_ms: null,
      model_id: null,
      model_display_name: null,
      status: 'completed',
      finish_reason: null,
      error_code: null,
      error_message: null,
      sequence_number: maxSeq + 1,
      attachments: [],
      created_at: new Date().toISOString(),
    };
    appendMsg(chatId, optimistic);

    // Set isSending + isStreaming immediately
    setSlice(chatId, { isSending: true, isStreaming: true, startedAt: Date.now() });

    // Sending failsafe (20min)
    const existingTimer = sendingTimers.get(chatId);
    if (existingTimer) clearTimeout(existingTimer);
    sendingTimers.set(chatId, setTimeout(() => {
      const slice = getStore().slicesByChat[chatId];
      if (slice?.isSending) {
        setSlice(chatId, { isSending: false });
      }
      sendingTimers.delete(chatId);
    }, SEND_FAILSAFE_MS));

    sendsInFlight.add(chatId); // FEAT-053 TASK-045 (QA-039-A)
    try {
      const sendReq: SendMessageRequest = { content: req.content, attachment_ids: req.attachment_ids };
      if (req.model_id) sendReq.model_id = req.model_id;
      const data = await sendChatMessage(chatId, sendReq);
      sendsInFlight.delete(chatId); // POST accepted: from here the connection may open (FEAT-053 TASK-045, QA-039-A)

      // Replace optimistic with server message
      const um: Message = {
        ...data.user_message,
        parent_message_id: null,
        content_blocks: data.user_message.content_blocks as ContentBlock[],
        tokens_in: null,
        tokens_out: null,
        cost_estimate_usd: null,
        latency_ms: null,
        model_id: null,
        model_display_name: null,
        finish_reason: null,
        error_code: null,
        error_message: null,
        attachments: [],
      };
      replaceOptimisticMsg(chatId, tempId, um);

      // Now open the stream (NO-OP if already connected)
      streamManager.ensureStream(chatId, projectId);
    // --- FEAT-053 TASK-045 (erro de envio) ---
    } catch (err) {
      sendsInFlight.delete(chatId); // FEAT-053 TASK-045 (QA-039-A)
      // Remove optimistic, clear sending state
      removeOptimisticMsg(chatId, tempId);
      setSlice(chatId, { isSending: false, isStreaming: false });
      const timer = sendingTimers.get(chatId);
      if (timer) { clearTimeout(timer); sendingTimers.delete(chatId); }
      // The refusal must reach the caller (useProjectChat.sendError) so the screen can warn the user.
      throw err;
    }
    // --- fim FEAT-053 TASK-045 (erro de envio) ---
  },

  /**
   * User-initiated stop (abort). Does NOT reconcile from server.
   */
  stop(chatId: string): void {
    const conn = connsByChat.get(chatId);
    if (conn) {
      conn.abort.abort();
    }
    // Also tell the server to abort
    void abortStream(chatId).catch(() => {});
  },

  /**
   * FEAT-053 TASK-045: dismiss the "turn ended before the reply started" warning of a chat.
   */
  clearTurnError(chatId: string): void {
    if (getStore().slicesByChat[chatId]?.turnError) {
      setSlice(chatId, { turnError: null });
    }
  },

  /**
   * Read current slice (non-reactive, for imperative checks).
   */
  getSlice(chatId: string): ChatStreamSlice | undefined {
    return getStore().slicesByChat[chatId];
  },

  /**
   * Check if a connection is live for this chatId.
   */
  hasActiveConnection(chatId: string): boolean {
    return connsByChat.has(chatId);
  },

  /**
   * Clear isSending for a chat (called when server confirms new turn).
   */
  clearSending(chatId: string): void {
    const slice = getStore().slicesByChat[chatId];
    if (slice?.isSending) {
      setSlice(chatId, { isSending: false });
    }
    const timer = sendingTimers.get(chatId);
    if (timer) { clearTimeout(timer); sendingTimers.delete(chatId); }
  },

  /**
   * TEST-ONLY: reset all module-level state so tests don't leak connections
   * or stale QueryClient references between test cases.
   */
  _resetForTests(): void {
    // Set shutdown flag first so any in-flight finalizeTurn/runConsumeLoop bails out
    _shuttingDown = true;
    // Zero reconnect delays so any in-flight reconnect cycle resolves instantly
    RECONNECT_DELAYS_MS = [0, 0, 0, 0, 0];
    // Abort and clear all live connections
    for (const [, conn] of connsByChat) {
      if (conn.watchdog) clearInterval(conn.watchdog);
      conn.abort.abort();
    }
    connsByChat.clear();
    // Clear all sending failsafe timers
    for (const [, timer] of sendingTimers) {
      clearTimeout(timer);
    }
    sendingTimers.clear();
    sendsInFlight.clear(); // FEAT-053 TASK-045 (QA-039-A)
    // Clear the QueryClient reference
    qc = null;
    // Reset the Zustand store
    useStreamStore.setState({ slicesByChat: {} });
  },

  /**
   * TEST-ONLY: configure reconnect delays (use [0,0,...] for fast tests).
   */
  _setReconnectDelays(delays: readonly number[]): void {
    RECONNECT_DELAYS_MS = delays;
  },
};
